/**
 * Archivo: MensajesServicios.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 2024-07-10
 * 
 * Descripción:
 * Este fichero contiene las funciones para interactuar con la API de mensajes. 
 * Incluye funciones para listar, añadir y eliminar mensajes fijos, de usuario y globales.
 * 
 * Propósito:
 * Proveer funciones para gestionar los datos de los mensajes, incluyendo su interacción con la base de datos a través del backend.
 */
const API = process.env.REACT_APP_API_BASE_URL; 

// Función para obtener los mensajes fijos de la base de datos
export const listaMensajesFijos = async (token) => {
    const urlMensajesFijos = `${API}/api/mensajes/fijos`;
    try {
        const response = await fetch(urlMensajesFijos, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        const data = await response.json();
        if (data.success) {
            return { success: true, data: data.data };
        } else {
            return { success: false, message: data.message };
        }
    } catch (error) {
        console.error('Error al enviar la solicitud:', error);
        return { success: false, message: 'Error al enviar la solicitud' };
    }
};

// Función para obtener los mensajes de un usuario
export const listaMensajesUsuario = async (token) => {
    const urlMensajesUsuario = `${API}/api/mensajes/usuario`;

    try {
        const response = await fetch(urlMensajesUsuario, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        const data = await response.json();
        //console.log("Respuesta API en servicios: ", data)
        if (data.message === 'OK') {
            return { success: true, data: data.body, contador: data.contador };
        } else {
            return { success: false, message: data.message };
        }
    } catch (error) {
        console.error('Error al enviar la solicitud:', error);
        return { success: false, message: 'Error al enviar la solicitud' };
    }
};

// Función para añadir un mensaje asociado a un usuario
export const añadirMensajeUsuario = async (token, id_mensaje, id_colegial, mensajePersonalizado = null) => {
    const urlAddMensajeUsuario = `${API}/api/mensajes/usuario`;
    //console.log("Peticion para enviar un mensajes a un usuario:", id_colegial, id_mensaje)

    try {
        const response = await fetch(urlAddMensajeUsuario, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ id_mensaje, id_colegial, texto_mensaje: mensajePersonalizado }),
        });

        const data = await response.json();
        if (data.success) {
            return { success: true, data: data.data };
        } else {
            return { success: false, message: data.message };
        }
    } catch (error) {
        console.error('Error al enviar la solicitud:', error);
        return { success: false, message: 'Error al enviar la solicitud' };
    }
};

// Función para añadir un mensaje global
export const añadirMensajeGlobal = async (token, texto_mensaje) => {
    const urlAddMensajeGlobal = `${API}/api/mensajes/global`;
    
    try {
        const response = await fetch(urlAddMensajeGlobal, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ texto_mensaje }),
        });

        const data = await response.json();
        if (data.success) {
            return { success: true, data: data.data };
        } else {
            return { success: false, message: data.message };
        }
    } catch (error) {
        console.error('Error al enviar la solicitud:', error);
        return { success: false, message: 'Error al enviar la solicitud' };
    }
};

// Función para eliminar un mensaje concreto de un usuario
export const eliminarMensajeUsuario = async (token, id_mensaje, id_usuario) => {
    const urlEliminarMensajeUsuario = `${API}/api/mensajes/usuario`;

    try {
        const response = await fetch(urlEliminarMensajeUsuario, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ id_mensaje, id_usuario }),
        });

        const data = await response.json();
        if (data.success) {
            return { success: true, data: data.data };
        } else {
            return { success: false, message: data.message };
        }
    } catch (error) {
        console.error('Error al enviar la solicitud:', error);
        return { success: false, message: 'Error al enviar la solicitud' };
    }
};

// Función para eliminar un mensaje global
export const eliminarMensajeGlobal = async (token, id_mensaje) => {
    const urlEliminarMensajeGlobal = `${API}/api/mensajes/global`;

    try {
        const response = await fetch(urlEliminarMensajeGlobal, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ id_mensaje }),
        });

        const data = await response.json();
        if (data.success) {
            return { success: true, data: data.data };
        } else {
            return { success: false, message: data.message };
        }
    } catch (error) {
        console.error('Error al enviar la solicitud:', error);
        return { success: false, message: 'Error al enviar la solicitud' };
    }
};

// Función para obtener los mensajes globales de la base de datos
export const listaMensajesGlobales = async (token) => {
    const urlMensajesGlobales = `${API}/api/mensajes/global`;

    try {
        const response = await fetch(urlMensajesGlobales, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
    
        const data = await response.json();
        if (data.success) {
            return { success: true, data: data.data };
        } else {
            return { success: false, message: data.message };
        }
    } catch (error) {
        console.error('Error al enviar la solicitud:', error);
        return { success: false, message: 'Error al enviar la solicitud' };
    }
};

export const getMensajesNuevos = async (token) => {
    const urlMensajesNuevos = `${API}/api/mensajes/nuevos`;

    try {
        const response = await fetch(urlMensajesNuevos, {
            method: 'GET',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
        const data = await response.json();
        if (data.success) {
            return { success: true, data: data.data };
        } else {
            return { success: false, message: data.message };
        }
    } catch (error) {
        console.error('Error al obtener los mensajes nuevos:', error);
        return { success: false, message: 'Error al obtener los mensajes nuevos' };
    }
};
